import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
	Card,
	CircularProgress,
	Grid,
	IconButton,
	styled,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";

import { type MachineGroup } from "@/schemas";
import type { Machine } from "@/schemas/machine";

import { ConfigurationPageLayout } from "../../components/configuration-page-layout";
import { MachineGroupNewButton } from "../components/machine-group-new-button";
import { MachineGroupEditButton } from "../components/machine-group-edit-button";
import { MachineGroupDeleteButton } from "../components/machine-group-delete-button";
import { MachineGroupHistoryButton } from "../components/machine-group-history-button";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(even)": {
		backgroundColor: theme.palette.action.hover,
	},
}));

export type ConfiguredMachines = Record<string, string[]>;

export interface MachineGroupsTemplateProps {
	readonly machineGroups: MachineGroup[];
	readonly configuredMachines: ConfiguredMachines;
	readonly machineList: Machine[];
	readonly isLoading: boolean;
	readonly isError: boolean;
	readonly onRetry: () => void;
}

export const MachineGroupsTemplate: React.FC<MachineGroupsTemplateProps> = ({
	machineGroups,
	configuredMachines,
	machineList,
	isLoading,
	isError,
	onRetry,
}) => {
	const intl = useIntl();

	return (
		<ConfigurationPageLayout
			title={intl.formatMessage({ id: "Machine Groups", defaultMessage: "Machine Groups" })}
			actions={<MachineGroupNewButton />}
		>
			<Card component={Grid} container direction="column">
				<TableContainer sx={{ flex: 1 }}>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell>
									<FormattedMessage id="Name" defaultMessage="Name" />
								</TableCell>
								<TableCell>
									<FormattedMessage id="Description" defaultMessage="Description" />
								</TableCell>
								<TableCell>
									<FormattedMessage id="Configured Machines" defaultMessage="Configured Machines" />
								</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading === true && (
								<StyledTableRow sx={{ height: 100 }}>
									<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
										<CircularProgress color="info" data-testid="machine-groups__progress__loading" />
									</TableCell>
								</StyledTableRow>
							)}
							{isError === true && isLoading === false && (
								<StyledTableRow sx={{ height: 100 }}>
									<TableCell colSpan={1000} align="center" sx={{ borderBottom: "none" }}>
										<Grid container justifyContent="center" alignItems="center" gap={2}>
											<Typography component="span" color="error" data-testid="machine-groups__text__error">
												<FormattedMessage
													id="Failed to load machine groups. Please try again."
													defaultMessage="Failed to load machine groups. Please try again."
												/>
											</Typography>
											<IconButton onClick={onRetry} data-testid="machine-groups__button__retry">
												<Refresh />
											</IconButton>
										</Grid>
									</TableCell>
								</StyledTableRow>
							)}
							{!isLoading && !isError && machineGroups.length === 0 && (
								<StyledTableRow>
									<TableCell colSpan={1000} align="center">
										<Typography color="textDisabled" data-testid="machine-groups__text__no-machine-groups">
											<FormattedMessage
												id="No Machine Groups Configured"
												defaultMessage="No Machine Groups Configured"
											/>
										</Typography>
									</TableCell>
								</StyledTableRow>
							)}
							{!isLoading &&
								!isError &&
								machineGroups.map((machineGroup) => (
									<StyledTableRow key={machineGroup.id}>
										<TableCell sx={{ fontWeight: 500 }}>{machineGroup.alias}</TableCell>
										<TableCell>{machineGroup.description}</TableCell>
										<TableCell>{(configuredMachines[machineGroup.id] ?? []).join(", ")}</TableCell>
										<TableCell>
											<Grid container gap={1} justifyContent="flex-end">
												<MachineGroupHistoryButton machineGroup={machineGroup} machineList={machineList} />
												<MachineGroupEditButton machineGroup={machineGroup} />
												<MachineGroupDeleteButton machineGroup={machineGroup} />
											</Grid>
										</TableCell>
									</StyledTableRow>
								))}
						</TableBody>
					</Table>
				</TableContainer>
			</Card>
		</ConfigurationPageLayout>
	);
};
